import React, { useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../ui/dialog";
import { Button } from "../../../ui/button";
import { Alert, AlertDescription } from "../../../ui/alert";
import { Card, CardContent } from "../../../ui/card";
import { Upload, FileText } from "lucide-react";
import { FlowData, SimpleFlowFormat } from "../utils/types";
import { isSimpleFormat, convertSimpleFormatToFlowData } from "../utils/utils";

interface ImportFlowModalProps {
  visible: boolean;
  onCancel: () => void;
  onImport: (flowData: FlowData) => void;
}

const ImportFlowModal: React.FC<ImportFlowModalProps> = ({
  visible,
  onCancel,
  onImport,
}) => {
  const [importData, setImportData] = useState<FlowData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isSimpleFormatDetected, setIsSimpleFormatDetected] = useState(false);

  const handleFileUpload = useCallback((file: File) => {
    setIsValidating(true);
    setError(null);
    setImportData(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = typeof e.target?.result === "string" ? e.target.result : "";
        const parsed = JSON.parse(content);
        
        let flowData: FlowData;
        let isSimple = false;
        
        // Check if it's simple format
        if (isSimpleFormat(parsed)) {
          isSimple = true;
          
          // Validate simple format
          if (!parsed.startNode) {
            throw new Error("Invalid simple flow format. Missing required 'startNode' field.");
          }
          
          if (!Array.isArray(parsed.nodes) || parsed.nodes.length === 0) {
            throw new Error("Invalid simple flow format. Missing or empty nodes array.");
          }
          
          // Validate nodes
          for (const node of parsed.nodes) {
            if (!node.type || !node.content || !node.identifier) {
              throw new Error("Invalid node in simple format. Each node must have type, content, and identifier.");
            }
            
            if (!["bot", "user", "decision"].includes(node.type)) {
              throw new Error(`Invalid node type "${node.type}". Must be "bot", "user", or "decision".`);
            }
            
            if (node.type === "decision" && (!node.conditions || !Array.isArray(node.conditions))) {
              throw new Error("Decision nodes must have conditions array.");
            }
          }
          
          // Check if startNode exists
          if (!parsed.nodes.find((n: any) => n.identifier === parsed.startNode || n.id === parsed.startNode)) {
            throw new Error(`Start node "${parsed.startNode}" not found in nodes array.`);
          }
          
          // Convert simple format to full format
          flowData = convertSimpleFormatToFlowData(parsed as SimpleFlowFormat);
        } else {
          // Validate full format structure
          if (!parsed.data) {
            throw new Error("Invalid flow file format. Missing required 'data' field.");
          }
          
          if (!parsed.data.nodes || !Array.isArray(parsed.data.nodes)) {
            throw new Error("Invalid flow file format. Missing or invalid nodes data.");
          }
          
          if (!parsed.data.edges || !Array.isArray(parsed.data.edges)) {
            throw new Error("Invalid flow file format. Missing or invalid edges data.");
          }
          
          // Validate node structure
          for (const node of parsed.data.nodes) {
            if (!node.id || !node.data) {
              throw new Error("Invalid node structure in flow file.");
            }
          }
          
          // Generate version and timestamp if missing
          flowData = {
            version: parsed.version || "1.0.0",
            timestamp: parsed.timestamp || new Date().toISOString(),
            metadata: {
              ...(parsed.metadata ?? {}),
              status: parsed.metadata?.status || "draft"
            },
            data: {
              nodes: parsed.data.nodes || [],
              edges: parsed.data.edges || [],
              globalVariableRegistry: parsed.data.globalVariableRegistry || {},
              decisionVariablesRegistry: parsed.data.decisionVariablesRegistry || {},
              identifiers: parsed.data.identifiers || [],
              interjectionGroups: parsed.data.interjectionGroups || [],
              nodeIdCounter: parsed.data.nodeIdCounter || 0,
              isFirstNode: parsed.data.isFirstNode || false,
              systemPrompt: parsed.data.systemPrompt
            }
          };
        }
        
        setImportData(flowData);
        setIsSimpleFormatDetected(isSimple);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to parse flow file");
        setImportData(null);
        setIsSimpleFormatDetected(false);
      } finally {
        setIsValidating(false);
      }
    };
    
    reader.onerror = () => {
      setError("Failed to read file");
      setIsValidating(false);
    };
    
    reader.readAsText(file);
    return false; // Prevent default upload behavior
  }, []);

  const handleImport = () => {
    if (importData) {
      onImport(importData);
      handleCancel();
    }
  };

  const handleCancel = () => {
    setImportData(null);
    setError(null);
    setIsValidating(false);
    setIsSimpleFormatDetected(false);
    onCancel();
  };

  const uploadProps = {
    name: "file",
    multiple: false,
    accept: ".json",
    beforeUpload: handleFileUpload,
    showUploadList: false,
  };

  return (
    <Dialog open={visible} onOpenChange={handleCancel}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-lg font-gc-med text-foreground">
            Import Conversation Flow
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          <div className="space-y-2">
            <h4 className="text-base font-gc-med text-foreground">Upload Flow JSON File</h4>
            <p className="text-sm text-muted-foreground">
              Select a conversation flow JSON file to import. Supports both the full format and the new simplified format for easy hand-writing. This will replace your current flow.
            </p>
          </div>

          <Card className="border-2 border-dashed border-border hover:border-muted-foreground/50 transition-colors">
            <CardContent className="p-8">
              <div 
                className="text-center cursor-pointer"
                onClick={() => {
                  const input = document.createElement("input");
                  input.type = "file";
                  input.accept = ".json";
                  input.onchange = (e) => {
                    const file = (e.target as HTMLInputElement).files?.[0];
                    if (file) handleFileUpload(file);
                  };
                  input.click();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const file = e.dataTransfer.files[0];
                  if (file) handleFileUpload(file);
                }}
                onDragOver={(e) => e.preventDefault()}
              >
                <div className="flex flex-col items-center space-y-4">
                  <Upload className="w-12 h-12 text-muted-foreground" />
                  <div className="space-y-2">
                    <p className="text-base font-gc-reg text-foreground">
                      Click or drag flow JSON file to this area to upload
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Only .json files are supported. Use either the full format or the new simplified format for easy tree creation.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {error && (
            <Alert className="border-red-200 bg-red-50">
              <AlertDescription className="text-red-800">
                <strong>Import Error:</strong> {error}
              </AlertDescription>
            </Alert>
          )}

          {importData && !error && (
            <Alert className="border-green-200 bg-green-50">
              <AlertDescription className="text-green-800">
                <div className="space-y-2">
                  <div className="font-medium">
                    Flow Ready to Import
                    {isSimpleFormatDetected && (
                      <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-md">
                        Simple Format ✨
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    <span>{importData.data.nodes.length} nodes, {importData.data.edges.length} edges</span>
                  </div>
                  <div className="text-sm text-green-600">
                    Version: {importData.version} | 
                    Timestamp: {new Date(importData.timestamp).toLocaleString()} |
                    Status: {importData.metadata?.status || "unknown"}
                  </div>
                  {isSimpleFormatDetected && (
                    <div className="text-sm text-blue-700 bg-blue-50 p-2 rounded">
                      <strong>Simple Format Detected:</strong> Your user-friendly JSON has been automatically converted to the full format with proper positioning and metadata.
                    </div>
                  )}
                  {importData.metadata?.title && (
                    <div className="text-sm">
                      <strong>Title:</strong> {importData.metadata.title}
                    </div>
                  )}
                  {importData.metadata?.description && (
                    <div className="text-sm">
                      <strong>Description:</strong> {importData.metadata.description}
                    </div>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={!importData || isValidating}
            className={isValidating ? "opacity-50" : ""}
          >
            {isValidating ? "Validating..." : "Import Flow"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportFlowModal;